import { notification } from 'antd'
import dayjs from 'dayjs'
import Timezone from 'dayjs/plugin/timezone'
import UTC from 'dayjs/plugin/utc'
import moment from 'moment'
import React from 'react'
import {
  all,
  call,
  fork,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects'
import * as apiActions from './api'
import * as appActions from './appActions'
import { reactNodeToText, recordAnalyticsEvent } from '../modules/utils'
import { getReportReceivedDate } from '../reducers/analytics'
import {
  CALCULATE,
  LAUNCH_CALCULATOR,
  QUIZ_SUBMIT,
} from '../constants/analytics'

dayjs.extend(UTC)
dayjs.extend(Timezone)

export const REFRESH_ANALYTICS_DATA = 'REFRESH_ANALYTICS_DATA'
export const SET_ANALYTICS_DATA = 'SET_ANALYTICS_DATA'
export const SET_ANALYTICS_LOADING_STATUS = 'SET_ANALYTICS_LOADING_STATUS'
export const SET_ANALYTICS_LOAD_ERROR = 'SET_ANALYTICS_LOAD_ERROR'
export const GET_ANALYTICS_SHARE_DATA = 'GET_ANALYTICS_SHARE_DATA'
export const SET_ANALYTICS_SHARE_DATA = 'SET_ANALYTICS_SHARE_DATA'
export const EXPORT_EVENTS_TABLE = 'EXPORT_EVENTS_TABLE'
export const EXPORT_CALCULATIONS = 'EXPORT_CALCULATIONS'
export const EXPORT_FORM_SUBMISSIONS = 'EXPORT_FORM_SUBMISSIONS'
export const EXPORT_QUIZ_SUBMISSIONS = 'EXPORT_QUIZ_SUBMISSIONS'
export const EXPORT_QUIZ_PERFORMANCE = 'EXPORT_QUIZ_PERFORMANCE'
export const EXPORT_GROUP_ACTIVITY = 'EXPORT_GROUP_ACTIVITY'

/* Format the data received from the analytics api call.
 *  @param   analyticsView   {string}    The name of the analytics view. i.e. "AnalyticsOverview"
 *  @param   analyticsData   {string}    The unformatted data object
 */
function formatAnalyticsDataForView(analyticsView, analyticsData) {
  /* Currently, this function just pads the data set with zero values if a date
   *  does not exist in the data set.
   *  We bypass it here for AssetDetail and ShareDetail.
   */
  if (
    analyticsView === 'AssetsDetail' ||
    analyticsView === 'ShareDetail' ||
    analyticsView === 'SharedEndpointUsage'
  )
    return { data: analyticsData }

  // Pad Empty dates with Empty data objects
  const dateValues = Object.keys(analyticsData)

  for (let idx = 0; idx < dateValues.length; idx++) {
    if (idx === dateValues.length - 1) continue

    const startDate = moment(dateValues[idx])
    const endDate = moment(dateValues[idx + 1])
    const daysBetweenDates = endDate.diff(startDate, 'days')

    for (let dayIdx = 1; dayIdx < daysBetweenDates; dayIdx++) {
      startDate.add(1, 'days')
      analyticsData[startDate.format('YYYY-MM-DD')] = {}
    }
  }

  // Sort by date
  const sortedAnalyticsData = {}
  Object.keys(analyticsData)
    .sort()
    .forEach(function (key) {
      sortedAnalyticsData[key] = analyticsData[key]
    })

  // Should be in format expected by UI code.
  return { data: sortedAnalyticsData }
}

/* Set Analytics Data in redux for the given analytics view.
 *  @param   analyticsView   {string}    The name of the analytics view. i.e. "AnalyticsOverview"
 *  @param   analyticsData   {string}    The formatted data object
 */
export function setAnalyticsDataForView(analyticsReport) {
  return {
    type: SET_ANALYTICS_DATA,
    analyticsReport,
  }
}

/* Refresh Analytics Data for the given analytics view.
 *  @param   analyticsView   {string}    The name of the analytics view. i.e. "AnalyticsOverview"
 */
export function refreshAnalyticsDataForView(analyticsView) {
  return {
    type: REFRESH_ANALYTICS_DATA,
    analyticsView,
  }
}

/**
 *
 * @param {number|undefined} dateReceived - timestamp when the report was received in milliseconds
 * @returns {boolean} - true if the report is less than an hour old, false otherwise
 */
function dataIsCurrent(dateReceived) {
  if (!dateReceived) return false
  const now = new Date()
  const lastFetched = new Date(dateReceived)
  const diff = now.getTime() - lastFetched.getTime()
  const diffHours = diff / (1000 * 60 * 60)
  return diffHours < 1
}

export function* refreshAnalyticsDataForViewSaga() {
  try {
    // first, check to see if the data has been grabbed in the last hour

    const reportReceivedDate = yield select(getReportReceivedDate)

    if (dataIsCurrent(reportReceivedDate)) {
      return
    }

    // Page-level indicator: AnalyticsPlotView renders a spinner while this
    // is true (and starting a load clears any previous load error).
    yield put({ type: SET_ANALYTICS_LOADING_STATUS, loading: true })

    const config = yield select((state) => state.config.appConfig)
    const { results } = yield call(apiActions.secureFetchSaga, {
      url: `${config.baseUrl}/api/analytics/report`,
      init: {
        method: 'GET',
      },
    })

    // if the report call returned a report request, fetch the data from that
    let analyticsData = results
    if (results.reportRequest) {
      analyticsData = yield call(apiActions.fetchSaga, {
        url: results.reportRequest,
        init: {
          method: 'GET',
        },
      })
    }

    const formattedResults = Object.keys(analyticsData).reduce(
      (acc, curr) => {
        acc[curr + 'Data'] = {
          ...formatAnalyticsDataForView(curr, analyticsData[curr]),
        }
        return acc
      },
      { reportReceived: Date.now() },
    )

    yield put(setAnalyticsDataForView(formattedResults))
  } catch (err) {
    console.error(err)
    // Surface the failure in-page (error panel with retry/support actions in
    // AnalyticsPlotView) instead of a toast over zeroed-out charts that read
    // as "no usage".
    yield put({
      type: SET_ANALYTICS_LOAD_ERROR,
      error:
        (err && err.message) || 'The analytics report could not be loaded.',
    })
  } finally {
    yield put({ type: SET_ANALYTICS_LOADING_STATUS, loading: false })
  }
}

export function setAnalyticsShareData(shareDataList) {
  return {
    type: SET_ANALYTICS_SHARE_DATA,
    shareDataList,
  }
}

export function getAnalyticsShareData(shareIds) {
  return {
    type: GET_ANALYTICS_SHARE_DATA,
    shareIds,
  }
}

export function* getAnalyticsShareDataSaga(action) {
  const { shareIds } = action

  if (!Array.isArray(shareIds) || !shareIds.length) return

  try {
    // Async Action Spinner
    yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })

    // Config for the API call
    const config = yield select((state) => state.config.appConfig)

    // Fetch
    const fetchURL = `${config.baseUrl}/api/assets/sharedByIds`
    const result = yield call(apiActions.secureFetchSaga, {
      url: fetchURL,
      init: {
        method: 'POST',
        body: JSON.stringify({ shareIds }),
      },
    })

    // Set
    yield put(setAnalyticsShareData(result))
  } catch (err) {
    // Error
    console.log(err)
  } finally {
    // Hide Spinner
    yield put({ type: appActions.COMPLETE_ASYNC })
  }
}

/**
 * @param {Object} tableData the source table data to export
 * @param {moment.Moment[]} dateRange pair of moments that represent the selected date range.
 * This can be a single value if a date has been selected.
 * @param {"shares"|"users"|"views"} view the view being exported
 */
export function exportEventsTable(data) {
  return {
    type: EXPORT_EVENTS_TABLE,
    data,
  }
}

function exportCSV(headers, subHeaders, dataToExport, filename) {
  // write data - inspired by https://stackoverflow.com/a/24922761
  const headersStr = headers ? `${headers.join(',')}\n` : ''
  const subHeadersStr = subHeaders ? `${subHeaders.join(',')}\n` : ''
  const encodedData = `${headersStr}${subHeadersStr}${dataToExport
    .map((item) =>
      Object.values(item)
        .map((val) => {
          if (typeof val === 'string') {
            val = val.replace(/"/g, '""')

            if (/([",\n])/g.test(val)) {
              val = `"${val}"`
            }
          }

          return val
        })
        .join(','),
    )
    .join('\n')}`

  const blob = new Blob(['\ufeff' + encodedData], {
    type: 'text/csv;charset=utf-8;',
  })

  // write to file
  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, filename)
  } else {
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    if (link.download !== 'undefined') {
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      window.open(url)
    }
  }
}

export function* exportEventsTableSaga(action) {
  const columnsToExport = {
    userDays: ['email', 'name', 'date'],
    assetViews: ['title', 'filename', 'file_type', 'email', 'name', 'date'],
    assetShares: [
      'title',
      'filename',
      'file_type',
      'email',
      'name',
      'date',
      'expires',
      'firstView',
      'totalViews',
    ],
    calcViews: ['title', 'email', 'name', 'date'],
    calculations: ['title', 'email', 'name', 'date'],
    submissions: ['date', 'email', 'name', 'title'],
    sharedEndpointUsage: [
      'consumer',
      'title',
      'filename',
      'eventType',
      'email',
      'platform',
      'date',
    ],
  }
  const headersToExport = {
    userDays: ['User Email', 'User Name', 'Date'],
    assetViews: [
      'Asset Title',
      'Filename',
      'File Type',
      'User Email',
      'User Name',
      'Date',
    ],
    assetShares: [
      'Asset Title',
      'Filename',
      'File Type',
      'User Email',
      'User Name',
      'Date',
      'Expires',
      'First View',
      'Total Views',
    ],
    calcViews: ['Calculator Title', 'User Email', 'User Name', 'Date'],
    calculations: ['Calculator Title', 'User Email', 'User Name', 'Date'],
    submissions: ['Date', 'User Email', 'User Name', 'Form Title'],
    sharedEndpointUsage: [
      'Consumer',
      'Asset Title',
      'Filename',
      'Event',
      'User',
      'Platform',
      'Date',
    ],
  }
  const exportTitles = {
    userDays: 'User_Days',
    assetViews: 'Asset_Views',
    assetShares: 'Asset_Shares',
    calcViews: 'Calculator_Views',
    calculations: 'Calculations',
    submissions: 'Form_Submissions',
    sharedEndpointUsage: 'Shared_Endpoint_Usage',
  }
  const { data } = action
  const {
    view,
    dateRange,
    selectedAsset,
    selectedUser,
    selectedGroupName,
    events,
    bamPlatform,
  } = data
  const columns = columnsToExport[view]
  const headers = headersToExport[view]
  const exportTitle = exportTitles[view]

  if (!columns) {
    console.warn(`Unsupported view ${view}`)
    return
  }

  const addShareData = (item, shareData) => {
    if (shareData) {
      const itemShare = shareData.find(
        (shareDataItem) => shareDataItem.ID === item.shareId,
      )
      if (itemShare) {
        item.expires = !itemShare.expires
          ? '-'
          : moment(itemShare.expires * 1000).format('YYYY-MM-DD')
        item.firstView = !itemShare.firstView
          ? '-'
          : moment(itemShare.firstView * 1000).format('YYYY-MM-DD')
        item.totalViews = !itemShare.totalViews ? '-' : itemShare.totalViews
      }
    }
    return item
  }

  try {
    yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })

    let shareData
    if (view === 'assetShares') {
      const shareIds = events.map((ev) => ev.shareId).filter((item) => !!item)
      const shareIdsBody = JSON.stringify({ shareIds })

      // Config for the API call
      const config = yield select((state) => state.config.appConfig)

      // Use the current hostname when available (config.mask) to ensure a same-origin
      // request through CloudFront, avoiding CORS preflight issues on custom domains.
      const effectiveBaseUrl = config.mask
        ? `https://${config.mask}`
        : config.baseUrl

      // Fetch
      const fetchURL = `${effectiveBaseUrl}/api/assets/sharedByIds`
      const result = yield call(apiActions.secureFetchSaga, {
        url: fetchURL,
        init: {
          method: 'POST',
          body: shareIdsBody,
        },
      })
      shareData = result
    }

    let dataToExport = events
      .map((item) => addShareData(item, shareData))
      .map((item) =>
        columns.reduce(
          (acc, curr) => ({
            ...acc,
            [curr]: React.isValidElement(item[curr])
              ? reactNodeToText(item[curr])
              : item[curr],
          }),
          {},
        ),
      )

    // filename
    const platform =
      bamPlatform === 'all' ? '' : `-${bamPlatform.toUpperCase()}`
    const assetTxt = !selectedAsset
      ? ''
      : `-${selectedAsset.replaceAll(/\.(.*)/gi, '').replaceAll(/ /gi, '_')}`
    const userTxt = !selectedUser
      ? ''
      : `-${selectedUser.name.replaceAll(/\s+/gi, '_')}`
    const groupTxt = !selectedGroupName
      ? ''
      : `-${selectedGroupName.replace(/\s+/g, '_')}`
    const dateFormat = 'YYYYMMDD'
    const dateTxt = Array.isArray(dateRange)
      ? `${dateRange[0].format(dateFormat)}-${dateRange[1].format(dateFormat)}`
      : moment(dateRange).format(dateFormat)
    const filename = `BAM${platform}-${exportTitle}${assetTxt}${userTxt}${groupTxt}-${dateTxt}.csv`

    exportCSV(headers, null, dataToExport, filename)
  } catch (err) {
    console.error(err)
  } finally {
    yield put({ type: appActions.COMPLETE_ASYNC })
  }
}

export function exportCalculations(data) {
  return {
    type: EXPORT_CALCULATIONS,
    data,
  }
}

export function exportFormSubmissions(data) {
  return {
    type: EXPORT_FORM_SUBMISSIONS,
    data,
  }
}

export function exportQuizSubmissions(data) {
  return {
    type: EXPORT_QUIZ_SUBMISSIONS,
    data,
  }
}

export function exportQuizPerformance(data) {
  return {
    type: EXPORT_QUIZ_PERFORMANCE,
    data,
  }
}

export function exportGroupActivity(data) {
  return {
    type: EXPORT_GROUP_ACTIVITY,
    data,
  }
}

export function* exportCalculationsSaga(action) {
  const { data } = action
  const { dateRange, selectedUser, events, bamPlatform, calculatorTitle } = data

  const inputs = []
  const inputKeys = []
  const outputs = []
  const outputKeys = []

  for (let event of events.toReversed()) {
    const eventKeys = Object.keys(event).sort()
    for (let key of eventKeys) {
      if (key.startsWith('in|')) {
        const tokens = key.split('|')
        const inputName = tokens[2]
        if (!inputs.includes(inputName)) {
          inputs.push(inputName)
          inputKeys.push(key)
        }
      } else if (key.startsWith('out|')) {
        const tokens = key.split('|')
        const outputName = tokens[2]
        if (!outputs.includes(outputName)) {
          outputs.push(outputName)
          outputKeys.push(key)
        }
      }
    }
  }

  const headers = ['Date', 'User Name', 'User Email']
  const subHeaders = ['', '', '']
  if (inputs.length) {
    inputs.forEach((inputName) => {
      headers.push('')
      subHeaders.push(inputName)
    })
    headers[3] = 'Inputs'
  }
  if (outputs.length) {
    outputs.forEach((outputName) => {
      headers.push('')
      subHeaders.push(outputName)
    })
    headers[3 + inputs.length] = 'Outputs'
  }

  try {
    yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })

    // gather inputs and output columns
    // get pretty names for inputs and outputs
    let dataToExport = events.map((event) => {
      return {
        date: event.date,
        user: event.user,
        email: event.email,
        ...inputs.reduce((prev, curr) => {
          const keyForCurrentEvent = Object.keys(event).find((key) =>
            key.endsWith(`|${curr}`),
          )
          prev[curr] = event[keyForCurrentEvent] || '-'
          return prev
        }, {}),
        ...outputs.reduce((prev, curr) => {
          const keyForCurrentEvent = Object.keys(event).find((key) =>
            key.endsWith(`|${curr}`),
          )
          prev[curr] = event[keyForCurrentEvent] || '-'
          return prev
        }, {}),
      }
    })

    // filename
    const platform =
      bamPlatform === 'all' ? '' : `-${bamPlatform.toUpperCase()}`
    const calcTxt = !calculatorTitle
      ? ''
      : `-${calculatorTitle.replaceAll(/\.(.*)/gi, '').replaceAll(/ /gi, '_')}`
    const userTxt = !selectedUser
      ? ''
      : `-${selectedUser.name.replaceAll(/\s+/gi, '_')}`
    const dateFormat = 'YYYYMMDD'
    const dateTxt = Array.isArray(dateRange)
      ? `${dateRange[0].format(dateFormat)}-${dateRange[1].format(dateFormat)}`
      : moment(dateRange).format(dateFormat)
    const filename = `BAM${platform}-${'Calculations'}${calcTxt}${userTxt}-${dateTxt}.csv`

    exportCSV(headers, subHeaders, dataToExport, filename)
  } catch (err) {
    console.error(err)
  } finally {
    yield put({ type: appActions.COMPLETE_ASYNC })
  }
}

export function* exportFormSubmissionsSaga(action) {
  const { data } = action
  const { dateRange, selectedUser, events, bamPlatform, formTitle } = data

  // Check if events have custom export columns (no 'in|' prefixed keys)
  const hasCustomColumns =
    events.length > 0 &&
    !Object.keys(events[0]).some((key) => key.startsWith('in|'))

  let headers, subHeaders, dataToExport

  if (hasCustomColumns) {
    // Custom export columns mode: use event keys as headers
    const baseKeys = ['date', 'user', 'email']
    const customKeys =
      events.length > 0
        ? Object.keys(events[0]).filter((key) => !baseKeys.includes(key))
        : []

    headers = ['Date', 'User Name', 'User Email', ...customKeys]
    subHeaders = null // No sub-headers needed for custom columns

    dataToExport = events.map((event) => {
      const row = {
        date: event.date,
        user: event.user,
        email: event.email,
      }
      for (const key of customKeys) {
        row[key] = event[key] || '-'
      }
      return row
    })
  } else {
    // Default mode: discover input/output fields from 'in|NN|fieldName' and 'out|NN|fieldName' patterns
    const inputs = []
    const inputKeys = []
    const outputs = []
    const outputKeys = []

    for (let event of events.toReversed()) {
      const eventKeys = Object.keys(event).sort()
      for (let key of eventKeys) {
        if (key.startsWith('in|')) {
          const tokens = key.split('|')
          const inputName = tokens[2]
          if (!inputs.includes(inputName)) {
            inputs.push(inputName)
            inputKeys.push(key)
          }
        } else if (key.startsWith('out|')) {
          const tokens = key.split('|')
          const outputName = tokens[2]
          if (!outputs.includes(outputName)) {
            outputs.push(outputName)
            outputKeys.push(key)
          }
        }
      }
    }

    headers = ['Date', 'User Name', 'User Email']
    subHeaders = ['', '', '']
    if (inputs.length) {
      inputs.forEach((inputName) => {
        headers.push('')
        subHeaders.push(inputName)
      })
      headers[3] = 'Inputs'
    }
    if (outputs.length) {
      const outputStartIdx = headers.length
      outputs.forEach((outputName) => {
        headers.push('')
        subHeaders.push(outputName)
      })
      headers[outputStartIdx] = 'Results'
    }

    dataToExport = events.map((event) => {
      return {
        date: event.date,
        user: event.user,
        email: event.email,
        ...inputs.reduce((prev, curr) => {
          const keyForCurrentEvent = Object.keys(event).find((key) =>
            key.endsWith(`|${curr}`),
          )
          prev[curr] = event[keyForCurrentEvent] || '-'
          return prev
        }, {}),
        ...outputs.reduce((prev, curr) => {
          const keyForCurrentEvent = Object.keys(event).find((key) =>
            key.endsWith(`|${curr}`),
          )
          prev[curr] = event[keyForCurrentEvent] || '-'
          return prev
        }, {}),
      }
    })
  }

  try {
    yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })

    // filename
    const platform =
      bamPlatform === 'all' ? '' : `-${bamPlatform.toUpperCase()}`
    const formTxt = !formTitle
      ? ''
      : `-${formTitle.replaceAll(/\.(.*)/gi, '').replaceAll(/ /gi, '_')}`
    const userTxt = !selectedUser
      ? ''
      : `-${selectedUser.name.replaceAll(/\s+/gi, '_')}`
    const dateFormat = 'YYYYMMDD'
    const dateTxt = Array.isArray(dateRange)
      ? `${dateRange[0].format(dateFormat)}-${dateRange[1].format(dateFormat)}`
      : moment(dateRange).format(dateFormat)
    const filename = `BAM${platform}-${'FormSubmissions'}${formTxt}${userTxt}-${dateTxt}.csv`

    exportCSV(headers, subHeaders, dataToExport, filename)
  } catch (err) {
    console.error(err)
  } finally {
    yield put({ type: appActions.COMPLETE_ASYNC })
  }
}

export function* exportQuizSubmissionsSaga(action) {
  const { data } = action
  const { events, dateRange, bamPlatform } = data

  try {
    yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })

    const headers = [
      'Quiz',
      'Date',
      'User Name',
      'User Email',
      'Score',
      'Passed',
    ]
    const columns = ['quiz', 'date', 'user', 'email', 'score', 'passed']

    const dataToExport = events.map(function (event) {
      return columns.reduce(function (acc, col) {
        acc[col] = event[col]
        return acc
      }, {})
    })

    const platform =
      bamPlatform === 'all' ? '' : '-' + bamPlatform.toUpperCase()
    const dateFormat = 'YYYYMMDD'
    const dateTxt = Array.isArray(dateRange)
      ? dateRange[0].format(dateFormat) + '-' + dateRange[1].format(dateFormat)
      : moment(dateRange).format(dateFormat)
    const filename = 'BAM' + platform + '-QuizSubmissions-' + dateTxt + '.csv'

    exportCSV(headers, null, dataToExport, filename)
  } catch (err) {
    console.error(err)
  } finally {
    yield put({ type: appActions.COMPLETE_ASYNC })
  }
}

export function* exportQuizPerformanceSaga(action) {
  const { data } = action
  const { questions, dateRange, bamPlatform } = data

  try {
    yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })

    const headers = ['Question', '% Correct']
    const columns = ['question', 'pct']

    const dataToExport = questions.map(function (q) {
      return columns.reduce(function (acc, col) {
        acc[col] = q[col]
        return acc
      }, {})
    })

    const platform =
      bamPlatform === 'all' ? '' : '-' + bamPlatform.toUpperCase()
    const dateFormat = 'YYYYMMDD'
    const dateTxt = Array.isArray(dateRange)
      ? dateRange[0].format(dateFormat) + '-' + dateRange[1].format(dateFormat)
      : moment(dateRange).format(dateFormat)
    const filename = 'BAM' + platform + '-QuizPerformance-' + dateTxt + '.csv'

    exportCSV(headers, null, dataToExport, filename)
  } catch (err) {
    console.error(err)
  } finally {
    yield put({ type: appActions.COMPLETE_ASYNC })
  }
}

export function* exportGroupActivitySaga(action) {
  const { data } = action
  const { rows, dateRange, bamPlatform } = data

  try {
    yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })

    const platform =
      bamPlatform === 'all' ? '' : '-' + bamPlatform.toUpperCase()
    const dateFormat = 'YYYYMMDD'
    const dateTxt = Array.isArray(dateRange)
      ? dateRange[0].format(dateFormat) + '-' + dateRange[1].format(dateFormat)
      : moment(dateRange).format(dateFormat)

    const headers = ['Group', 'Days Active']
    const columns = ['group', 'daysActive']
    const filename = 'BAM' + platform + '-GroupActivity-' + dateTxt + '.csv'

    const dataToExport = rows.map(function (row) {
      return columns.reduce(function (acc, col) {
        acc[col] = row[col]
        return acc
      }, {})
    })

    exportCSV(headers, null, dataToExport, filename)
  } catch (err) {
    console.error(err)
  } finally {
    yield put({ type: appActions.COMPLETE_ASYNC })
  }
}

function* watchRefreshAnalytics() {
  yield all([
    takeLatest(REFRESH_ANALYTICS_DATA, refreshAnalyticsDataForViewSaga),
    takeLatest(
      appActions.SET_ACTIVE_ANALYTICS_DURATION,
      refreshAnalyticsDataForViewSaga,
    ),
  ])
}

function* calculatorTask(action) {
  try {
    yield fork(recordAnalyticsEvent, action.type, action.data)
  } catch (error) {
    console.log(error)
  }
}

// Main Export
export default function* analyticsSagas() {
  yield all([
    fork(watchRefreshAnalytics),
    takeEvery(GET_ANALYTICS_SHARE_DATA, getAnalyticsShareDataSaga),
    takeLatest(EXPORT_EVENTS_TABLE, exportEventsTableSaga),
    takeLatest(EXPORT_CALCULATIONS, exportCalculationsSaga),
    takeLatest(EXPORT_FORM_SUBMISSIONS, exportFormSubmissionsSaga),
    takeLatest(EXPORT_QUIZ_SUBMISSIONS, exportQuizSubmissionsSaga),
    takeLatest(EXPORT_QUIZ_PERFORMANCE, exportQuizPerformanceSaga),
    takeLatest(EXPORT_GROUP_ACTIVITY, exportGroupActivitySaga),
    takeEvery(CALCULATE, calculatorTask),
    takeEvery(LAUNCH_CALCULATOR, calculatorTask),
    takeEvery(QUIZ_SUBMIT, calculatorTask),
  ])
}
